import { LenderDocumentTypeEnum, CardCaptureStepEnum, AssetTypesEnum, DocumentCaptureStepEnum } from '@types';
import { MARINE_PHOTO_LIST } from '@constants';
import { getAssetSpecificContent } from '@utils';

/**
 * Returns true if the target array contains at least one element from the pattern array.
 */
export function containsAny(target: string[], pattern: string[]): boolean {
  return pattern.some((item) => target.includes(item));
}

export const VEHICLE_EXTERNAL_PHOTO_KEYS = [
  'ASSET_BACK',
  'ASSET_FRONT',
  'ASSET_LEFT',
  'ASSET_RIGHT'
] as const;

export const MARINE_EXTERNAL_PHOTO_KEYS = [
  'MARINE_FRONT',
  'MARINE_BACK',
  'MARINE_LEFT',
  'MARINE_RIGHT'
] as const;

const INTERNAL_PHOTO_DOCUMENT_TYPES = [
  LenderDocumentTypeEnum.INTERNAL_BACK,
  LenderDocumentTypeEnum.INTERNAL_BOOT,
  LenderDocumentTypeEnum.INTERNAL_FRONT,
  LenderDocumentTypeEnum.INTERNAL_LIVING_AREA,
  LenderDocumentTypeEnum.INTERNAL_KITCHEN,
  LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA
] as const;

const EXTERNAL_PHOTO_DOCUMENT_TYPES = [
  LenderDocumentTypeEnum.ASSET_BACK,
  LenderDocumentTypeEnum.ASSET_FRONT,
  LenderDocumentTypeEnum.ASSET_LEFT,
  LenderDocumentTypeEnum.ASSET_RIGHT
] as const;

const MARINE_PHOTO_DOCUMENT_TYPES = [
  LenderDocumentTypeEnum.MARINE_FRONT,
  LenderDocumentTypeEnum.MARINE_BACK,
  LenderDocumentTypeEnum.MARINE_LEFT,
  LenderDocumentTypeEnum.MARINE_RIGHT
] as const;

/**
 * Whether any vehicle external photos have been previously uploaded.
 */
export function hasAnyVehicleExternalPhoto(
  documents: Record<string, any> | undefined
): boolean {
  if (!documents) return false;
  return VEHICLE_EXTERNAL_PHOTO_KEYS.some((key) => !!documents[key]);
}

/**
 * Whether any marine external photos have been previously uploaded.
 */
export function hasAnyMarineExternalPhoto(
  documents: Record<string, any> | undefined
): boolean {
  if (!documents) return false;
  return MARINE_EXTERNAL_PHOTO_KEYS.some((key) => !!documents[key]);
}

export interface ExternalPhotoSteps {
  vehicleBack: { isDone: boolean };
  vehicleFront: { isDone: boolean };
  vehicleDriver: { isDone: boolean };
  vehiclePassenger: { isDone: boolean };
}

/**
 * Required vehicle exterior photos (per template) are complete and none of
 * those required sides need resubmission. Sides not in documentTypes are ignored
 * so a 2-side template still shows the Vehicle hub green tick when both are done.
 */
export function isExternalPhotosSuccess(
  steps: ExternalPhotoSteps,
  documents: Record<string, any> | undefined,
  documentTypes: string[]
): boolean {
  if (countConfiguredVehicleExternalPhotos(documentTypes) === 0) return false;
  if (!areAllVehicleAssetPhotosDone(documentTypes, steps)) return false;
  return !VEHICLE_EXTERNAL_PHOTO_KEYS.some(
    (docType) =>
      documentTypes.includes(docType) && documents?.[docType]?.needsResubmit
  );
}

const VEHICLE_EXTERNAL_PHOTO_ERROR_CHECKS: {
  docType: (typeof VEHICLE_EXTERNAL_PHOTO_KEYS)[number];
  stepKey: keyof ExternalPhotoSteps;
}[] = [
  { docType: 'ASSET_BACK', stepKey: 'vehicleBack' },
  { docType: 'ASSET_FRONT', stepKey: 'vehicleFront' },
  { docType: 'ASSET_LEFT', stepKey: 'vehiclePassenger' },
  { docType: 'ASSET_RIGHT', stepKey: 'vehicleDriver' }
];

/**
 * A required vehicle exterior photo needs resubmission (not done + flagged).
 * Sides not in documentTypes are ignored so a leftover ASSET_LEFT flag on a
 * front/back-only template does not show an error next to a green tick.
 */
export function hasExternalPhotosError(
  steps: ExternalPhotoSteps,
  documents: Record<string, any> | undefined,
  documentTypes: string[]
): boolean {
  return VEHICLE_EXTERNAL_PHOTO_ERROR_CHECKS.some(
    ({ docType, stepKey }) =>
      documentTypes.includes(docType) &&
      !steps[stepKey].isDone &&
      documents?.[docType]?.needsResubmit
  );
}

/**
 * Whether the external photos button should be hidden for regular vehicles.
 */
export function isExternalPhotosHidden(
  documentTypes: string[],
  assetType: string,
  vehicleMode?: string
): boolean {
  return (
    !containsAny(documentTypes, [...EXTERNAL_PHOTO_DOCUMENT_TYPES]) ||
    (assetType === AssetTypesEnum.MARINE_WITH_TRAILER && vehicleMode === 'watercraft') ||
    assetType === AssetTypesEnum.MARINE
  );
}

/**
 * Whether the marine external photos button should be hidden.
 */
export function isMarineExternalPhotosHidden(
  documentTypes: string[],
  assetType: string,
  vehicleMode?: string
): boolean {
  return (
    !containsAny(documentTypes, [...MARINE_PHOTO_DOCUMENT_TYPES]) ||
    (assetType === AssetTypesEnum.MARINE_WITH_TRAILER && vehicleMode === 'trailer') ||
    (assetType !== AssetTypesEnum.MARINE && assetType !== AssetTypesEnum.MARINE_WITH_TRAILER)
  );
}

/**
 * Whether the internal photos button should be hidden.
 */
export function isInternalPhotosHidden(documentTypes: string[]): boolean {
  return !containsAny(documentTypes, [...INTERNAL_PHOTO_DOCUMENT_TYPES]);
}

/**
 * Whether the odometer button should be hidden.
 * Hidden when ODOMETER is not required, or when internal photos are included.
 */
export function isOdometerHidden(documentTypes: string[]): boolean {
  return (
    !documentTypes.includes(LenderDocumentTypeEnum.ODOMETER) ||
    containsAny(documentTypes, [...INTERNAL_PHOTO_DOCUMENT_TYPES])
  );
}

const INTERNAL_PHOTO_RESUBMIT_KEYS = [
  LenderDocumentTypeEnum.ODOMETER,
  ...INTERNAL_PHOTO_DOCUMENT_TYPES
] as const;

/**
 * Any required internal photo document needs resubmission.
 * When documentTypes is provided, only template-required docs are considered
 * so a leftover flag on a non-required side does not show a false error icon.
 */
export function hasInternalPhotosError(
  documents: Record<string, any> | undefined,
  documentTypes?: string[]
): boolean {
  if (!documents) return false;
  return INTERNAL_PHOTO_RESUBMIT_KEYS.some(
    (key) =>
      (!documentTypes || documentTypes.includes(key)) &&
      documents[key]?.needsResubmit === true
  );
}

/** Docs that drive the non-marine Welcome Vehicle row red icon. */
const VEHICLE_SECTION_RESUBMIT_KEYS = [
  LenderDocumentTypeEnum.VIN,
  LenderDocumentTypeEnum.REGO_PLATE,
  LenderDocumentTypeEnum.ODOMETER,
  LenderDocumentTypeEnum.OPERATING_HOURS,
  LenderDocumentTypeEnum.SALE_DETAILS,
  LenderDocumentTypeEnum.ASSET_BACK,
  LenderDocumentTypeEnum.ASSET_FRONT,
  LenderDocumentTypeEnum.ASSET_LEFT,
  LenderDocumentTypeEnum.ASSET_RIGHT,
  LenderDocumentTypeEnum.INTERNAL_BACK,
  LenderDocumentTypeEnum.INTERNAL_BOOT,
  LenderDocumentTypeEnum.INTERNAL_FRONT,
  LenderDocumentTypeEnum.INTERNAL_LIVING_AREA,
  LenderDocumentTypeEnum.INTERNAL_KITCHEN,
  LenderDocumentTypeEnum.INTERNAL_SLEEPING_AREA
] as const;

/**
 * Welcome Vehicle row error icon: any template-required vehicle-section doc
 * needs resubmission (includes campervan living/kitchen/sleeping).
 */
export function hasVehicleSectionNeedsResubmit(
  documents: Record<string, { needsResubmit?: boolean } | undefined> | undefined,
  documentTypes?: string[]
): boolean {
  if (!documents) return false;
  return VEHICLE_SECTION_RESUBMIT_KEYS.some(
    (key) =>
      (!documentTypes || documentTypes.includes(key)) &&
      documents[key]?.needsResubmit === true
  );
}

export interface InternalPhotoSteps {
  odometer: { isDone: boolean };
  vehicleFrontSeats: { isDone: boolean };
  vehicleBackSeats: { isDone: boolean };
  vehicleBoot: { isDone: boolean };
  vehicleLivingArea: { isDone: boolean };
  vehicleKitchen: { isDone: boolean };
  vehicleSleepingArea: { isDone: boolean };
}

/**
 * All required internal photos are complete.
 */
export function isInternalPhotosSuccess(
  steps: InternalPhotoSteps,
  documentTypes: string[]
): boolean {
  return (
    (!documentTypes.includes(CardCaptureStepEnum.odometer) || steps.odometer.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.internalBack) || steps.vehicleBackSeats.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.internalFront) || steps.vehicleFrontSeats.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.internalBoot) || steps.vehicleBoot.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.internalLivingArea) || steps.vehicleLivingArea.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.internalKitchen) || steps.vehicleKitchen.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.internalSleepingArea) || steps.vehicleSleepingArea.isDone)
  );
}

export interface MarinePhotoSteps {
  marineFront: { isDone: boolean };
  marineBack: { isDone: boolean };
  marineLeft: { isDone: boolean };
  marineRight: { isDone: boolean };
}

const MARINE_EXTERNAL_PHOTO_STEP_MAP: Record<
  (typeof MARINE_EXTERNAL_PHOTO_KEYS)[number],
  keyof MarinePhotoSteps
> = {
  MARINE_FRONT: 'marineFront',
  MARINE_BACK: 'marineBack',
  MARINE_LEFT: 'marineLeft',
  MARINE_RIGHT: 'marineRight'
};

/**
 * All template-required marine external photos are complete and none of those
 * required sides need resubmission. Sides not in documentTypes are ignored so a
 * 3-side template still shows the green tick when all three are done.
 */
export function isMarineExternalPhotosSuccess(
  steps: MarinePhotoSteps,
  documents: Record<string, any> | undefined,
  documentTypes: string[]
): boolean {
  if (countConfiguredMarineExternalPhotos(documentTypes) === 0) return false;
  return MARINE_EXTERNAL_PHOTO_KEYS.every(
    (docType) =>
      !documentTypes.includes(docType) ||
      (steps[MARINE_EXTERNAL_PHOTO_STEP_MAP[docType]]?.isDone &&
        !documents?.[docType]?.needsResubmit)
  );
}

/**
 * A required marine exterior photo needs resubmission (not done + flagged).
 * Sides not in documentTypes are ignored.
 */
export function hasMarineExternalPhotosError(
  steps: MarinePhotoSteps,
  documents: Record<string, any> | undefined,
  documentTypes: string[]
): boolean {
  return MARINE_EXTERNAL_PHOTO_KEYS.some(
    (docType) =>
      documentTypes.includes(docType) &&
      !steps[MARINE_EXTERNAL_PHOTO_STEP_MAP[docType]]?.isDone &&
      documents?.[docType]?.needsResubmit
  );
}

/**
 * Counts how many vehicle external photos are configured in the inspection's documentTypes.
 */
export function countConfiguredVehicleExternalPhotos(documentTypes: string[]): number {
  return VEHICLE_EXTERNAL_PHOTO_KEYS.filter((key) => documentTypes.includes(key)).length;
}

/**
 * Whether the vehicle external photos are complete.
 */
export function isVehicleExternalPhotosComplete(documentTypes: string[]): boolean {
  return countConfiguredVehicleExternalPhotos(documentTypes) === 4;
}

/**
 * Counts how many marine external photos are configured in the inspection's documentTypes.
 */
export function countConfiguredMarineExternalPhotos(documentTypes: string[]): number {
  return MARINE_EXTERNAL_PHOTO_KEYS.filter((key) => documentTypes.includes(key)).length;
}

/**
 * Whether the marine external photos are complete.
 */
export function isMarineExternalPhotosComplete(documentTypes: string[]): boolean {
  return countConfiguredMarineExternalPhotos(documentTypes) === 4;
}

/**
 * Determines which step to navigate to when the vehicle external photos button is clicked.
 * Returns 'vehicles-photos' (gallery) if any photos already exist or not all 4 are configured,
 * and 'vehicles-photos-first' (camera) when all 4 are configured and none uploaded yet.
 */
export function getVehicleExternalPhotosStep(
  documents: Record<string, any> | undefined,
  documentTypes: string[]
): 'vehicles-photos' | 'vehicles-photos-first' {
  if (hasAnyVehicleExternalPhoto(documents) || !isVehicleExternalPhotosComplete(documentTypes)) {
    return 'vehicles-photos';
  }
  return 'vehicles-photos-first';
}

/**
 * Determines which step to navigate to when the marine external photos button is clicked.
 * Returns 'marine-photos' (gallery) if any photos already exist or not all 4 are configured,
 * and 'marine-photos-first' (camera) when all 4 are configured and none uploaded yet.
 */
export function getMarineExternalPhotosStep(
  documents: Record<string, any> | undefined,
  documentTypes: string[]
): 'marine-photos' | 'marine-photos-first' {
  if (hasAnyMarineExternalPhoto(documents) || !isMarineExternalPhotosComplete(documentTypes)) {
    return 'marine-photos';
  }
  return 'marine-photos-first';
}

export interface PhotoStepState {
  isDone: boolean;
  available?: boolean;
  [key: string]: any;
}

export interface RefreshInspectionHandlers {
  setData: (data: any) => void;
  setInspectionChecks: (data: any) => void;
  setVehicleImageFront: (data: any) => void;
  setVehicleImageDriver: (data: any) => void;
  setVehicleImagePassenger: (data: any) => void;
  setVehicleImageBack: (data: any) => void;
  setMarineImageFront: (data: any) => void;
  setMarineImageBack: (data: any) => void;
  setMarineImageLeft: (data: any) => void;
  setMarineImageRight: (data: any) => void;
}

export interface PhotoStepStates {
  vehicleFront: PhotoStepState;
  vehicleDriver: PhotoStepState;
  vehiclePassenger: PhotoStepState;
  vehicleBack: PhotoStepState;
  marineFront: PhotoStepState;
  marineBack: PhotoStepState;
  marineLeft: PhotoStepState;
  marineRight: PhotoStepState;
}

const PHOTO_STEP_MAP: { fileType: string; docKey: string; stateKey: keyof PhotoStepStates; setterKey: keyof RefreshInspectionHandlers }[] = [
  { fileType: 'ASSET_FRONT', docKey: 'ASSET_FRONT', stateKey: 'vehicleFront', setterKey: 'setVehicleImageFront' },
  { fileType: 'ASSET_RIGHT', docKey: 'ASSET_RIGHT', stateKey: 'vehicleDriver', setterKey: 'setVehicleImageDriver' },
  { fileType: 'ASSET_LEFT', docKey: 'ASSET_LEFT', stateKey: 'vehiclePassenger', setterKey: 'setVehicleImagePassenger' },
  { fileType: 'ASSET_BACK', docKey: 'ASSET_BACK', stateKey: 'vehicleBack', setterKey: 'setVehicleImageBack' },
  { fileType: 'MARINE_FRONT', docKey: 'MARINE_FRONT', stateKey: 'marineFront', setterKey: 'setMarineImageFront' },
  { fileType: 'MARINE_BACK', docKey: 'MARINE_BACK', stateKey: 'marineBack', setterKey: 'setMarineImageBack' },
  { fileType: 'MARINE_LEFT', docKey: 'MARINE_LEFT', stateKey: 'marineLeft', setterKey: 'setMarineImageLeft' },
  { fileType: 'MARINE_RIGHT', docKey: 'MARINE_RIGHT', stateKey: 'marineRight', setterKey: 'setMarineImageRight' }
];

/**
 * Updates photo step isDone states based on files from the API.
 */
export function updatePhotoStepsFromFiles(
  files: any[],
  docs: Record<string, any> | undefined,
  states: PhotoStepStates,
  handlers: Pick<RefreshInspectionHandlers, 'setVehicleImageFront' | 'setVehicleImageDriver' | 'setVehicleImagePassenger' | 'setVehicleImageBack' | 'setMarineImageFront' | 'setMarineImageBack' | 'setMarineImageLeft' | 'setMarineImageRight'>
): void {
  const fileTypes = files.map((f: any) => f.documentType);
  PHOTO_STEP_MAP.forEach(({ fileType, docKey, stateKey, setterKey }) => {
    if (fileTypes.includes(fileType) && !docs?.[docKey]?.needsResubmit) {
      handlers[setterKey]({ ...states[stateKey], isDone: true });
    }
  });
}

/**
 * Fetches latest inspection data, checks, and files from the API, then updates the store.
 * Falls back to window.location.reload() on failure.
 */
export function inspectionWithPreservedSalePrice(
  inspection: any,
  previous?: SalePriceContext | null,
  options?: { justSubmitted?: boolean }
): any {
  if (!inspection || !previous) return inspection;
  const previousPrice = getSubmittedSalePrice(
    previous.extraFields,
    previous.documents
  );
  const nextPrice = getSubmittedSalePrice(
    inspection.extraFields,
    inspection.documents
  );
  if (options?.justSubmitted && previousPrice != null) {
    // Always keep the price we just POSTed. An immediate GET can still return the
    // previous rejected value; treating that as a "newer lender correction" would
    // undo a successful resubmit. There is no revision/timestamp on the payload
    // to tell stale-old from a real correction.
    return {
      ...inspection,
      ...mergeSubmittedSalePrice(
        { extraFields: inspection.extraFields, documents: inspection.documents },
        previousPrice
      )
    };
  }
  if (previousPrice == null || nextPrice != null) return inspection;
  if (inspection.documents?.SALE_DETAILS?.needsResubmit) return inspection;
  return {
    ...inspection,
    extraFields: mergeSalePriceIntoExtraFields(inspection.extraFields, previousPrice)
  };
}

export async function refreshInspectionData(
  inspectionId: string,
  postFn: (url: string, data: any, useGlobalCatch?: boolean) => Promise<any>,
  apiPaths: { GET_INSPECTION: string; GET_INSPECTION_CHECK: string; GET_INSPECTION_FILES: string },
  states: PhotoStepStates,
  handlers: RefreshInspectionHandlers,
  previousSalePrice?: SalePriceContext
): Promise<void> {
  try {
    const [inspectionRes, checkRes, filesRes] = await Promise.all([
      postFn(apiPaths.GET_INSPECTION, { inspectionId }, false),
      postFn(apiPaths.GET_INSPECTION_CHECK, { inspectionId }, false),
      postFn(apiPaths.GET_INSPECTION_FILES, { inspectionId }, false)
    ]);
    if (inspectionRes?.inspection) {
      handlers.setData(
        inspectionWithPreservedSalePrice(inspectionRes.inspection, previousSalePrice)
      );
      if (filesRes?.files) {
        updatePhotoStepsFromFiles(
          filesRes.files,
          inspectionRes.inspection.documents,
          states,
          handlers
        );
      }
    }
    if (checkRes?.inspectionChecks) {
      handlers.setInspectionChecks(checkRes.inspectionChecks);
    }
  } catch {
    window.location.reload();
  }
}

export interface AllDocumentStepStates {
  vinObj: { isDone: boolean };
  numberPlate: { isDone: boolean };
  marineHin: { isDone: boolean };
  marineRegoNumber: { isDone: boolean };
  operatingHours: { isDone: boolean };
  vehicle: { isDone: boolean };
  vehicleFront: { isDone: boolean };
  vehicleBack: { isDone: boolean };
  vehicleDriver: { isDone: boolean };
  vehiclePassenger: { isDone: boolean };
  odometer: { isDone: boolean };
  vehicleFrontSeats: { isDone: boolean };
  vehicleBackSeats: { isDone: boolean };
  vehicleBoot: { isDone: boolean };
  vehicleLivingArea: { isDone: boolean };
  vehicleKitchen: { isDone: boolean };
  vehicleSleepingArea: { isDone: boolean };
  marineFront: { isDone: boolean };
  marineBack: { isDone: boolean };
  marineLeft: { isDone: boolean };
  marineRight: { isDone: boolean };
}

const ALL_DOCUMENT_STEP_CHECKS: { stepEnum: string; stateKey: keyof AllDocumentStepStates }[] = [
  { stepEnum: CardCaptureStepEnum.vin, stateKey: 'vinObj' },
  { stepEnum: CardCaptureStepEnum.plateNumber, stateKey: 'numberPlate' },
  { stepEnum: CardCaptureStepEnum.marineHin, stateKey: 'marineHin' },
  { stepEnum: CardCaptureStepEnum.marineRegoNumber, stateKey: 'marineRegoNumber' },
  { stepEnum: CardCaptureStepEnum.operatingHours, stateKey: 'operatingHours' },
  { stepEnum: CardCaptureStepEnum.assetFront, stateKey: 'vehicleFront' },
  { stepEnum: CardCaptureStepEnum.assetBack, stateKey: 'vehicleBack' },
  { stepEnum: CardCaptureStepEnum.assetLeft, stateKey: 'vehiclePassenger' },
  { stepEnum: CardCaptureStepEnum.assetRight, stateKey: 'vehicleDriver' },
  { stepEnum: CardCaptureStepEnum.odometer, stateKey: 'odometer' },
  { stepEnum: CardCaptureStepEnum.internalBack, stateKey: 'vehicleBackSeats' },
  { stepEnum: CardCaptureStepEnum.internalFront, stateKey: 'vehicleFrontSeats' },
  { stepEnum: CardCaptureStepEnum.internalBoot, stateKey: 'vehicleBoot' },
  { stepEnum: CardCaptureStepEnum.internalLivingArea, stateKey: 'vehicleLivingArea' },
  { stepEnum: CardCaptureStepEnum.internalKitchen, stateKey: 'vehicleKitchen' },
  { stepEnum: CardCaptureStepEnum.internalSleepingArea, stateKey: 'vehicleSleepingArea' },
  { stepEnum: CardCaptureStepEnum.marineFront, stateKey: 'marineFront' },
  { stepEnum: CardCaptureStepEnum.marineBack, stateKey: 'marineBack' },
  { stepEnum: CardCaptureStepEnum.marineLeft, stateKey: 'marineLeft' },
  { stepEnum: CardCaptureStepEnum.marineRight, stateKey: 'marineRight' }
];

export interface SalePriceContext {
  extraFields?: { saleDetails?: { vehicleSalePrice?: number | string | null } } | null;
  documents?: {
    SALE_DETAILS?: {
      needsResubmit?: boolean;
      extraFields?: { saleDetails?: { vehicleSalePrice?: number | string | null } };
    };
  } | null;
}

// Keep in sync with INVALID_DOCUMENTS_BY_ASSET[MARINE_WITH_TRAILER]:
// watercraft must not wait on VIN, odometer, operating hours, or internals.
const MARINE_SECTION_STATE_KEYS: (keyof AllDocumentStepStates)[] = [
  'marineHin',
  'marineRegoNumber',
  'marineFront',
  'marineBack',
  'marineLeft',
  'marineRight'
];

// Trailer hub on MARINE_WITH_TRAILER: VIN / plate / odometer / hours / ASSET_*.
// Sale Price and marine items belong on the Watercraft hub.
const TRAILER_SECTION_STATE_KEYS: (keyof AllDocumentStepStates)[] = [
  'vinObj',
  'numberPlate',
  'operatingHours',
  'odometer',
  'vehicleFront',
  'vehicleBack',
  'vehicleDriver',
  'vehiclePassenger'
];

const TRAILER_SECTION_DOC_TYPES = [
  LenderDocumentTypeEnum.VIN,
  LenderDocumentTypeEnum.REGO_PLATE,
  LenderDocumentTypeEnum.ODOMETER,
  LenderDocumentTypeEnum.OPERATING_HOURS,
  LenderDocumentTypeEnum.ASSET_FRONT,
  LenderDocumentTypeEnum.ASSET_BACK,
  LenderDocumentTypeEnum.ASSET_LEFT,
  LenderDocumentTypeEnum.ASSET_RIGHT
] as const;

export function pickVehicleSectionSteps(
  store: AllDocumentStepStates
): AllDocumentStepStates {
  return {
    vinObj: store.vinObj,
    numberPlate: store.numberPlate,
    marineHin: store.marineHin,
    marineRegoNumber: store.marineRegoNumber,
    operatingHours: store.operatingHours,
    vehicle: store.vehicle,
    vehicleFront: store.vehicleFront,
    vehicleBack: store.vehicleBack,
    vehicleDriver: store.vehicleDriver,
    vehiclePassenger: store.vehiclePassenger,
    odometer: store.odometer,
    vehicleFrontSeats: store.vehicleFrontSeats,
    vehicleBackSeats: store.vehicleBackSeats,
    vehicleBoot: store.vehicleBoot,
    vehicleLivingArea: store.vehicleLivingArea,
    vehicleKitchen: store.vehicleKitchen,
    vehicleSleepingArea: store.vehicleSleepingArea,
    marineFront: store.marineFront,
    marineBack: store.marineBack,
    marineLeft: store.marineLeft,
    marineRight: store.marineRight
  };
}

/**
 * Page title for the vehicle / watercraft / trailer hub after leaving a capture
 * step. MARINE_WITH_TRAILER uses mode-specific labels; everything else uses the
 * asset-type title from getAssetSpecificContent.
 */
export function getVehicleSectionPageTitle(
  assetType?: string | null,
  vehicleMode?: string | null
): string {
  if (assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
    return vehicleMode === 'trailer' ? 'Trailer' : 'Watercraft';
  }
  return getAssetSpecificContent(assetType as AssetTypesEnum).title;
}

export function getSalePriceContext(store: SalePriceContext): SalePriceContext {
  return { extraFields: store.extraFields, documents: store.documents };
}

export function mergeSalePriceIntoExtraFields(
  extraFields: SalePriceContext['extraFields'],
  amount: number
): NonNullable<SalePriceContext['extraFields']> {
  return {
    ...extraFields,
    saleDetails: {
      ...extraFields?.saleDetails,
      vehicleSalePrice: amount
    }
  };
}

export function parseSubmittedSalePrice(
  price: number | string | null | undefined
): number | null {
  if (price == null || price === '') return null;
  const raw = typeof price === 'string' ? price.replace(/,/g, '').trim() : price;
  if (raw === '') return null;
  const amount = Number(raw);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount;
}

export function getSubmittedSalePrice(
  extraFields?: SalePriceContext['extraFields'],
  documents?: SalePriceContext['documents']
): number | undefined {
  // Parse each source independently so an invalid extraFields value (e.g. 0 or '')
  // does not suppress a valid price stored on the SALE_DETAILS document.
  const fromExtraFields = parseSubmittedSalePrice(extraFields?.saleDetails?.vehicleSalePrice);
  if (fromExtraFields != null) return fromExtraFields;
  return parseSubmittedSalePrice(
    documents?.SALE_DETAILS?.extraFields?.saleDetails?.vehicleSalePrice
  ) ?? undefined;
}

export function mergeSubmittedSalePrice(
  store: SalePriceContext,
  amount: number
): {
  extraFields: NonNullable<SalePriceContext['extraFields']>;
  documents: NonNullable<SalePriceContext['documents']>;
} {
  return {
    extraFields: mergeSalePriceIntoExtraFields(store.extraFields, amount),
    documents: {
      ...store.documents,
      SALE_DETAILS: {
        ...store.documents?.SALE_DETAILS,
        extraFields: {
          ...store.documents?.SALE_DETAILS?.extraFields,
          saleDetails: {
            ...store.documents?.SALE_DETAILS?.extraFields?.saleDetails,
            vehicleSalePrice: amount
          }
        },
        needsResubmit: false
      }
    }
  };
}

/**
 * Sale Price is required when SALE_DETAILS is in the template. Unlike the other
 * vehicle items it has no capture step, so completeness comes from the inspection's
 * extraFields (or the SALE_DETAILS document payload) rather than the step store.
 * Matches the seller form: a price is complete only when it is a finite number > 0.
 */
export function isSalePriceRequirementMet(
  documentTypes: string[] | undefined,
  extraFields?: SalePriceContext['extraFields'],
  documents?: SalePriceContext['documents']
): boolean {
  if (!documentTypes?.includes(LenderDocumentTypeEnum.SALE_DETAILS)) return true;
  if (documents?.SALE_DETAILS?.needsResubmit) return false;
  return getSubmittedSalePrice(extraFields, documents) != null;
}

/**
 * Returns true when every required item in the vehicle section is complete,
 * including Sale Price when the template requires it.
 */
export function areAllVehicleSectionStepsComplete(
  documentTypes: string[],
  steps: AllDocumentStepStates,
  salePriceContext?: SalePriceContext
): boolean {
  const stepsDone = ALL_DOCUMENT_STEP_CHECKS.every(
    ({ stepEnum, stateKey }) => !documentTypes?.includes(stepEnum) || !!steps[stateKey]?.isDone
  );
  if (!stepsDone) return false;
  return isSalePriceRequirementMet(
    documentTypes,
    salePriceContext?.extraFields,
    salePriceContext?.documents
  );
}

/**
 * Watercraft-only completion: marine capture items plus Sale Price.
 * Trailer / car items are excluded so MARINE_WITH_TRAILER watercraft
 * does not wait on VIN or trailer photos.
 */
export function areMarineSectionStepsComplete(
  documentTypes: string[],
  steps: AllDocumentStepStates,
  salePriceContext?: SalePriceContext
): boolean {
  const marineChecks = ALL_DOCUMENT_STEP_CHECKS.filter(({ stateKey }) =>
    MARINE_SECTION_STATE_KEYS.includes(stateKey)
  );
  const stepsDone = marineChecks.every(
    ({ stepEnum, stateKey }) => !documentTypes?.includes(stepEnum) || !!steps[stateKey]?.isDone
  );
  if (!stepsDone) return false;
  return isSalePriceRequirementMet(
    documentTypes,
    salePriceContext?.extraFields,
    salePriceContext?.documents
  );
}

/**
 * Trailer-only completion for MARINE_WITH_TRAILER. Ignores Sale Price and
 * marine items so the Trailer hub / Welcome row can go green independently.
 */
export function areTrailerSectionStepsComplete(
  documentTypes: string[],
  steps: AllDocumentStepStates
): boolean {
  const trailerChecks = ALL_DOCUMENT_STEP_CHECKS.filter(({ stateKey }) =>
    TRAILER_SECTION_STATE_KEYS.includes(stateKey)
  );
  return trailerChecks.every(
    ({ stepEnum, stateKey }) => !documentTypes?.includes(stepEnum) || !!steps[stateKey]?.isDone
  );
}

export function hasTrailerSectionNeedsResubmit(
  documents: Record<string, { needsResubmit?: boolean } | undefined> | undefined,
  documentTypes?: string[]
): boolean {
  if (!documents) return false;
  return TRAILER_SECTION_DOC_TYPES.some(
    (key) =>
      (!documentTypes || documentTypes.includes(key)) &&
      documents[key]?.needsResubmit === true
  );
}

export function isVehicleSectionCompleteFromStore(
  documentTypes: string[],
  store: AllDocumentStepStates & SalePriceContext
): boolean {
  return areAllVehicleSectionStepsComplete(
    documentTypes,
    pickVehicleSectionSteps(store),
    getSalePriceContext(store)
  );
}

/**
 * Returns true if the current capture type is a bank statement,
 * or every required document step is complete (including Sale Price).
 */
export function areAllDocumentStepsComplete(
  type: string,
  documentTypes: string[],
  steps: AllDocumentStepStates,
  salePriceContext?: SalePriceContext
): boolean {
  if (DocumentCaptureStepEnum.bank === type) return true;
  return areAllVehicleSectionStepsComplete(documentTypes, steps, salePriceContext);
}

export function isSellerCaptureSectionComplete(
  store: AllDocumentStepStates &
    SalePriceContext & {
      documentTypes?: string[];
      assetType?: string;
      vehicleMode?: string;
    },
  type?: string
): boolean {
  if (type === DocumentCaptureStepEnum.bank) return true;
  const documentTypes = store.documentTypes ?? [];
  const steps = pickVehicleSectionSteps(store);
  const salePriceContext = getSalePriceContext(store);
  if (store.assetType === AssetTypesEnum.MARINE_WITH_TRAILER) {
    if (store.vehicleMode === 'watercraft') {
      return areMarineSectionStepsComplete(documentTypes, steps, salePriceContext);
    }
    if (store.vehicleMode === 'trailer') {
      return areTrailerSectionStepsComplete(documentTypes, steps);
    }
    // Unset / "default" mode is not a real MWT hub. Do not treat the combined
    // inspection as complete (would wait on Sale Price + marine + trailer, or
    // incorrectly auto-redirect). Welcome always sets watercraft|trailer first.
    return false;
  }
  return areAllVehicleSectionStepsComplete(documentTypes, steps, salePriceContext);
}

const VEHICLE_ASSET_PHOTO_TYPES: string[] = [
  CardCaptureStepEnum.assetFront,
  CardCaptureStepEnum.assetBack,
  CardCaptureStepEnum.assetLeft,
  CardCaptureStepEnum.assetRight
];

const MARINE_PHOTO_TYPES: string[] = [
  CardCaptureStepEnum.marineFront,
  CardCaptureStepEnum.marineBack,
  CardCaptureStepEnum.marineLeft,
  CardCaptureStepEnum.marineRight
];

const INTERNAL_PHOTO_TYPES: string[] = [
  CardCaptureStepEnum.internalFront,
  CardCaptureStepEnum.internalBack,
  CardCaptureStepEnum.internalBoot,
  CardCaptureStepEnum.internalLivingArea,
  CardCaptureStepEnum.internalKitchen,
  CardCaptureStepEnum.internalSleepingArea,
  CardCaptureStepEnum.odometer
];

export function isVehicleAssetPhotoType(type: string): boolean {
  return VEHICLE_ASSET_PHOTO_TYPES.includes(type);
}

export function isMarinePhotoType(type: string): boolean {
  return MARINE_PHOTO_TYPES.includes(type);
}

export function isInternalPhotoType(type: string): boolean {
  return INTERNAL_PHOTO_TYPES.includes(type);
}

/**
 * Whether all required vehicle asset photos (front/back/left/right) are complete.
 */
export function areAllVehicleAssetPhotosDone(
  documentTypes: string[],
  steps: {
    vehicleFront: { isDone: boolean };
    vehicleBack: { isDone: boolean };
    vehicleDriver: { isDone: boolean };
    vehiclePassenger: { isDone: boolean };
  }
): boolean {
  return (
    (!documentTypes.includes(CardCaptureStepEnum.assetFront) || steps.vehicleFront.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.assetBack) || steps.vehicleBack.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.assetLeft) || steps.vehiclePassenger.isDone) &&
    (!documentTypes.includes(CardCaptureStepEnum.assetRight) || steps.vehicleDriver.isDone)
  );
}

/** Multi-photo vehicle wizard positions → store keys. Matches VehiclePhoneMappings. */
export const VEHICLE_PHOTO_POSITION_KEYS: Record<
  number,
  keyof ExternalPhotoSteps
> = {
  1: 'vehicleFront',
  2: 'vehicleDriver',
  3: 'vehiclePassenger',
  4: 'vehicleBack'
};

const VEHICLE_PHOTO_TYPE_KEYS: Record<string, keyof ExternalPhotoSteps> = {
  [CardCaptureStepEnum.assetFront]: 'vehicleFront',
  [CardCaptureStepEnum.assetRight]: 'vehicleDriver',
  [CardCaptureStepEnum.assetLeft]: 'vehiclePassenger',
  [CardCaptureStepEnum.assetBack]: 'vehicleBack'
};

export function getVehiclePhotoStepKey(
  type: string,
  vehiclePosition?: number
): keyof ExternalPhotoSteps | undefined {
  if (VEHICLE_PHOTO_TYPE_KEYS[type]) return VEHICLE_PHOTO_TYPE_KEYS[type];
  if (type === CardCaptureStepEnum.vehiclePhoto && vehiclePosition) {
    return VEHICLE_PHOTO_POSITION_KEYS[vehiclePosition];
  }
  return undefined;
}

/** Multi-photo marine wizard positions → store keys. Matches MarinePhoneMappings. */
export const MARINE_PHOTO_POSITION_KEYS: Record<number, keyof MarinePhotoSteps> = {
  1: 'marineFront',
  2: 'marineRight',
  3: 'marineLeft',
  4: 'marineBack'
};

const MARINE_PHOTO_TYPE_KEYS: Record<string, keyof MarinePhotoSteps> = {
  [CardCaptureStepEnum.marineFront]: 'marineFront',
  [CardCaptureStepEnum.marineRight]: 'marineRight',
  [CardCaptureStepEnum.marineLeft]: 'marineLeft',
  [CardCaptureStepEnum.marineBack]: 'marineBack'
};

/**
 * Store key for the marine photo just captured. Used so CaptureResult /
 * CaptureComplete can mark isDone locally before GET_INSPECTION_FILES catches up.
 */
export function getMarinePhotoStepKey(
  type: string,
  vehiclePosition?: number
): keyof MarinePhotoSteps | undefined {
  if (MARINE_PHOTO_TYPE_KEYS[type]) return MARINE_PHOTO_TYPE_KEYS[type];
  if (type === CardCaptureStepEnum.marinePhoto && vehiclePosition) {
    return MARINE_PHOTO_POSITION_KEYS[vehiclePosition];
  }
  return undefined;
}

/**
 * Whether all configured marine photos in the photo list are complete.
 */
export function areAllConfiguredMarinePhotosDone(
  vehiclePhotoList: string[],
  steps: MarinePhotoSteps
): boolean {
  const stepMap: Record<string, { isDone: boolean }> = {
    MARINE_FRONT: steps.marineFront,
    MARINE_RIGHT: steps.marineRight,
    MARINE_LEFT: steps.marineLeft,
    MARINE_BACK: steps.marineBack
  };
  return vehiclePhotoList.every((photo) => stepMap[photo]?.isDone);
}

export interface CaptureGuidanceMessage {
  plainText?: string;
  boldSubject?: string;
}

const SCAN_MESSAGES: Record<string, string> = {
  [CardCaptureStepEnum.marineHin]: 'Make sure the HIN details are clear to read, with no blur or glare.',
  [CardCaptureStepEnum.marineRegoNumber]: 'Make sure the Registration Number details are clear to read, with no blur or glare.',
  [CardCaptureStepEnum.vin]: 'Make sure the VIN is clear to read, with no blur or glare.',
  [CardCaptureStepEnum.plateNumber]: 'Make sure your number plate details are clear to read, with no blur or glare.',
  [CardCaptureStepEnum.odometer]: 'Make sure the photo of your odometer is clear to read, with no blur or glare.',
  [CardCaptureStepEnum.operatingHours]: 'Make sure the photo of your operating hours is clear to read, with no blur or glare.',
  [DocumentCaptureStepEnum.bank]: 'Make sure all your details, including your account number and BSB are clear to read, with no blur or glare.',
  [DocumentCaptureStepEnum.marineRegoCert]: 'Make sure the registration certificate details are clear to read, with no blur or glare.'
};

const MARINE_PHOTO_SUBJECTS: Record<string, string> = {
  [CardCaptureStepEnum.marineFront]: 'front of the watercraft',
  [CardCaptureStepEnum.marineLeft]: 'port side of the watercraft',
  [CardCaptureStepEnum.marineRight]: 'starboard side of the watercraft',
  [CardCaptureStepEnum.marineBack]: 'back of the watercraft'
};

const MARINE_POSITION_SUBJECTS: Record<number, string> = {
  1: 'front of the watercraft',
  2: 'starboard side of the watercraft',
  3: 'port side of the watercraft',
  4: 'back of the watercraft'
};

/**
 * Returns the guidance message for the photo capture review screen.
 * For photo types, returns { boldSubject } which should be rendered as:
 *   "Make sure the <bold>{boldSubject}</bold> is clearly visible, with no obstructions, blur or glare."
 * For scan/document types, returns { plainText } as the full message string.
 */
export function getCaptureGuidanceMessage(
  type: string,
  assetType: string,
  vehiclePosition?: number
): CaptureGuidanceMessage {
  if (SCAN_MESSAGES[type]) {
    return { plainText: SCAN_MESSAGES[type] };
  }

  if (MARINE_PHOTO_SUBJECTS[type]) {
    return { boldSubject: MARINE_PHOTO_SUBJECTS[type] };
  }

  const asset = getAssetSpecificContent(assetType as AssetTypesEnum);

  if (type === CardCaptureStepEnum.vehiclePhoto && vehiclePosition) {
    const vehiclePositionSubjects: Record<number, string> = {
      1: `front of the ${asset.lowerTitle}`,
      2: `${asset.sides.driver.toLowerCase()} of the ${asset.lowerTitle}`,
      3: `${asset.sides.passenger.toLowerCase()} of the ${asset.lowerTitle}`,
      4: `back of the ${asset.lowerTitle}`
    };
    if (vehiclePositionSubjects[vehiclePosition]) {
      return { boldSubject: vehiclePositionSubjects[vehiclePosition] };
    }
  }

  if (type === CardCaptureStepEnum.marinePhoto && vehiclePosition) {
    if (MARINE_POSITION_SUBJECTS[vehiclePosition]) {
      return { boldSubject: MARINE_POSITION_SUBJECTS[vehiclePosition] };
    }
  }

  const assetPhotoSubjects: Record<string, string> = {
    [CardCaptureStepEnum.assetFront]: `front of the ${asset.lowerTitle}`,
    [CardCaptureStepEnum.assetLeft]: `${asset.sides.passenger.toLowerCase()} of the ${asset.lowerTitle}`,
    [CardCaptureStepEnum.assetRight]: `${asset.sides.driver.toLowerCase()} of the ${asset.lowerTitle}`,
    [CardCaptureStepEnum.assetBack]: `back of the ${asset.lowerTitle}`
  };
  if (assetPhotoSubjects[type]) {
    return { boldSubject: assetPhotoSubjects[type] };
  }

  const internalMessages: Record<string, string> = {
    [CardCaptureStepEnum.internalFront]: 'Make sure the front seats of the vehicle are clearly visible, with no obstructions, blur or glare.',
    [CardCaptureStepEnum.internalBack]: 'Make sure the back seats of the vehicle are clearly visible, with no obstructions, blur or glare.',
    [CardCaptureStepEnum.internalBoot]: 'Make sure the boot of the vehicle is clearly visible, with no obstructions, blur or glare.',
    [CardCaptureStepEnum.internalLivingArea]: `Make sure the photo of your ${asset.lowerTitle}'s main living area is clearly visible.`,
    [CardCaptureStepEnum.internalKitchen]: `Make sure the photo of your ${asset.lowerTitle}'s kitchen is clearly visible.`,
    [CardCaptureStepEnum.internalSleepingArea]: `Make sure the photo of your ${asset.lowerTitle}'s main sleeping area is clearly visible.`
  };
  if (internalMessages[type]) {
    return { plainText: internalMessages[type] };
  }

  return {};
}

const SCAN_SUCCESS_MESSAGES: Record<string, string> = {
  [CardCaptureStepEnum.vin]: 'Your VIN has been successfully scanned.',
  [CardCaptureStepEnum.plateNumber]: 'Your number plate has been successfully scanned.',
  [CardCaptureStepEnum.marineHin]: 'Your Watercraft HIN has been successfully scanned.',
  [CardCaptureStepEnum.marineRegoNumber]: 'Your Watercraft Registration Number has been successfully scanned.',
  [CardCaptureStepEnum.odometer]: 'Your odometer has been successfully scanned.',
  [CardCaptureStepEnum.operatingHours]: 'Your photo has been successfully uploaded.',
  [DocumentCaptureStepEnum.bank]: 'Your bank statement has been successfully scanned.'
};

const INTERNAL_SUCCESS_MESSAGES: Record<string, string> = {
  [CardCaptureStepEnum.internalFront]: 'The photo of the front seats of the vehicle has been successfully captured.',
  [CardCaptureStepEnum.internalBack]: 'The photo of the back seats area of the vehicle has been successfully captured.',
  [CardCaptureStepEnum.internalBoot]: 'The photo of the boot of the vehicle has been successfully captured.'
};

/**
 * Returns the success message shown after a capture completes.
 * For photo types, constructs "The photo of the {subject} has been successfully captured."
 * For scan/document types, returns a specific pre-defined message.
 * For multi-photo flows at the last position, returns an "all photos captured" message.
 */
export function getCaptureSuccessMessage(
  type: string,
  assetType: string,
  vehiclePosition?: number
): string {
  if (SCAN_SUCCESS_MESSAGES[type]) {
    return SCAN_SUCCESS_MESSAGES[type];
  }

  if (type === CardCaptureStepEnum.vehiclePhoto && vehiclePosition === 4) {
    return `Your ${getAssetSpecificContent(assetType as AssetTypesEnum).lowerTitle} photos have been successfully captured.`;
  }
  if (type === CardCaptureStepEnum.marinePhoto && vehiclePosition === 4) {
    return 'Your watercraft photos have been successfully captured.';
  }

  if (INTERNAL_SUCCESS_MESSAGES[type]) {
    return INTERNAL_SUCCESS_MESSAGES[type];
  }

  const asset = getAssetSpecificContent(assetType as AssetTypesEnum);
  const internalDynamic: Record<string, string> = {
    [CardCaptureStepEnum.internalLivingArea]: `The photo of the living area of the ${asset.lowerTitle} has been successfully captured.`,
    [CardCaptureStepEnum.internalKitchen]: `The photo of the kitchen of the ${asset.lowerTitle} has been successfully captured.`,
    [CardCaptureStepEnum.internalSleepingArea]: `The photo of the sleeping area of the ${asset.lowerTitle} has been successfully captured.`
  };
  if (internalDynamic[type]) {
    return internalDynamic[type];
  }

  const guidance = getCaptureGuidanceMessage(type, assetType, vehiclePosition);
  if (guidance.boldSubject) {
    return `The photo of the ${guidance.boldSubject} has been successfully captured.`;
  }

  return '';
}